/**
 * The app-wide snackbar: the one place a save says how it went.
 *
 * Any component, hook or helper calls `notify.success(t("…"))` and
 * `<SnackbarHost />`, mounted once in App.jsx, shows it at the top centre of
 * the screen. A plain emitter rather than a React context so that non-component
 * code (the wizard's resource/service helpers) can report too, and so a test
 * can mock this one module instead of wrapping every render in a provider.
 *
 * Messages arrive already translated — this module never closes over `t`.
 *
 * The three colours mean three different things, and pages must not pick them
 * by taste:
 * - success (green) — the change is stored.
 * - error (red)     — it is not, because something went wrong.
 * - warning (yellow) — it is not, but nothing is broken: the plan does not
 *   allow it, the account is suspended, a simulation may not touch it, or the
 *   form is missing something. The person has to do something different, not
 *   try again.
 */

export const NoticeSeverity = Object.freeze({
  SUCCESS: "success",
  WARNING: "warning",
  ERROR: "error",
});

const listeners = new Set();
let nextId = 1;

const emit = (severity, message) => {
  if (!message) return;
  const notice = { id: nextId++, severity, message };
  listeners.forEach((listener) => listener(notice));
};

export const notify = {
  success: (message) => emit(NoticeSeverity.SUCCESS, message),
  warning: (message) => emit(NoticeSeverity.WARNING, message),
  error: (message) => emit(NoticeSeverity.ERROR, message),
};

/** Returns the unsubscribe function, so it drops straight into a useEffect. */
export const subscribeToNotices = (listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Refusals that are a rule rather than a fault (see the module comment): a
 * plan limit (402, `PlanLimitExceededException`), and the two 403 fences in
 * Program.cs that answer with a `code`.
 */
const WARNING_CODES = new Set(["ACCOUNT_SUSPENDED", "IMPERSONATION_FORBIDDEN"]);

export const isWarningError = (err) =>
  err?.response?.status === 402 ||
  WARNING_CODES.has(err?.response?.data?.code);

/**
 * A failed write the caller has already worded. A warning-class refusal keeps
 * the API's own sentence when there is one, because only the API knows which
 * rule it hit (which plan limit, that a simulation may not touch billing).
 */
export const notifyFailure = (err, message) => {
  if (isWarningError(err)) {
    notify.warning(err.response.data?.message || message);
  } else {
    notify.error(message);
  }
};

/**
 * A failed write, worded by the API when it phrased one (API messages are
 * already in the reader's language) and by `fallbackMessage` otherwise.
 */
export const notifySaveError = (err, fallbackMessage) =>
  notifyFailure(err, err?.response?.data?.message || fallbackMessage);
